import { useState } from 'react';
import { format } from 'date-fns';
import { Minus, Plus } from 'lucide-react';
import { aud } from '@/utils/format';
import { SESSION_DEPOSIT_AUD, SESSION_META } from '@/domain/pricing';
import type { SelfBookSession } from '@/domain/selfBookFlow';

const ORDER: SelfBookSession[] = ['full-day', 'half-day', 'small'];

/** K4 custom hold: the duration's nearest standard bracket, kept as the session tag. */
function bracketFor(durationMin: number): SelfBookSession {
  if (durationMin <= 120) return 'small';
  if (durationMin <= 300) return 'half-day';
  return 'full-day';
}

function durationLabel(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h ? `${h}h` : ''}${h && m ? ' ' : ''}${m ? `${m}m` : h ? '' : '0m'}`.trim();
}

interface Props {
  artistName: string;
  startIso: string;
  onPick: (session: SelfBookSession, custom?: { depositAud: number; durationMin: number }) => void;
  onClose: () => void;
}

export default function HoldLengthChooser({ artistName, startIso, onPick, onClose }: Props) {
  const when = format(new Date(startIso), 'EEE d MMM · h:mmaaa');
  const [customOpen, setCustomOpen] = useState(false);
  const [durationMin, setDurationMin] = useState(210);
  const [depositAud, setDepositAud] = useState(450);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-overlay/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        data-testid="hold-length-chooser"
        className="w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl border border-gold-dim/40 bg-ink-deep p-5 text-cream-soft"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gold-dim/50 sm:hidden" />
        <h2 className="heading-serif text-base text-cream">{artistName}</h2>
        <p className="font-mono text-xs text-gold-bright mt-0.5">{when}</p>
        <p className="label-small-caps mt-4 mb-2">Hold this slot as</p>
        <div className="grid gap-2">
          {ORDER.map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`hold-length-${s}`}
              onClick={() => onPick(s)}
              className="flex items-center gap-3 rounded-lg border border-gold-dim/30 p-3 text-left hover:border-gold-bright/70"
            >
              <span className="flex-1">
                <span className="block text-sm font-medium text-cream">{SESSION_META[s].label}</span>
                <span className="block text-xs text-cream-faint">{SESSION_META[s].description}</span>
              </span>
              <span className="font-mono text-sm text-gold-bright">{aud(SESSION_DEPOSIT_AUD[s])} deposit</span>
            </button>
          ))}

          <button
            type="button"
            data-testid="hold-length-custom"
            aria-expanded={customOpen}
            onClick={() => setCustomOpen((v) => !v)}
            className="flex items-center gap-3 rounded-lg border border-gold-dim/30 p-3 text-left hover:border-gold-bright/70"
          >
            <span className="flex-1">
              <span className="block text-sm font-medium text-cream">Custom</span>
              <span className="block text-xs text-cream-faint">Set your own duration &amp; deposit</span>
            </span>
            <span className="font-mono text-sm text-gold-bright">{customOpen ? '−' : '+'}</span>
          </button>

          {customOpen ? (
            <div
              data-testid="hold-length-custom-body"
              className="rounded-lg border border-gold-bright/40 p-3 bg-ink-black/40"
            >
              <div className="flex items-center justify-between">
                <span className="label-small-caps">Duration</span>
                <span className="inline-flex items-center gap-1.5">
                  <button
                    type="button"
                    data-testid="custom-duration-down"
                    onClick={() => setDurationMin((m) => Math.max(30, m - 30))}
                    className="grid h-7 w-7 place-items-center rounded-md border border-gold-dim/40 text-cream-soft hover:border-gold-bright/60"
                  >
                    <Minus size={13} />
                  </button>
                  <span data-testid="custom-duration" className="w-16 text-center font-mono text-sm text-cream">
                    {durationLabel(durationMin)}
                  </span>
                  <button
                    type="button"
                    data-testid="custom-duration-up"
                    onClick={() => setDurationMin((m) => m + 30)}
                    className="grid h-7 w-7 place-items-center rounded-md border border-gold-dim/40 text-cream-soft hover:border-gold-bright/60"
                  >
                    <Plus size={13} />
                  </button>
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <label htmlFor="custom-deposit" className="label-small-caps">
                  Deposit
                </label>
                <span className="inline-flex items-center gap-1 font-mono text-sm text-cream">
                  <span className="text-cream-faint">$</span>
                  <input
                    id="custom-deposit"
                    data-testid="custom-deposit"
                    type="number"
                    min={0}
                    step={50}
                    value={depositAud}
                    onChange={(e) => setDepositAud(Math.max(0, Number(e.target.value) || 0))}
                    className="w-20 rounded-md border border-gold-dim/40 bg-ink-deep px-2 py-1 text-right text-cream"
                  />
                </span>
              </div>
              <button
                type="button"
                data-testid="custom-hold-confirm"
                onClick={() => onPick(bracketFor(durationMin), { depositAud, durationMin })}
                className="mt-3 w-full rounded-md border border-gold-bright bg-gold-bright px-3 py-2 font-inter text-sm font-semibold text-ink-black"
              >
                Hold slot — {aud(depositAud)}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
